import DescriptionModal from "../../Description/DescriptionModal";
import { createImg, createLoadingScreen } from "../../DomUtilities/ElementsCreator";
import LanguageUtil from "../../Utilities/LanguageUtil";
import Mediator from "../../Utilities/Mediator";

export default class VariantBaseModal {
	loadingScreen: HTMLElement;
	descriptionModal: DescriptionModal;

	constructor() {
		this.loadingScreen = createLoadingScreen();
	}

	protected getModalData(form: HTMLFormElement) {
		const inputs = form.elements;
		let variantFormData = new FormData();
		let valid: boolean = form.checkValidity();

		for (let i = 0; i < inputs.length; i++) {
			let input = (inputs[i] as HTMLInputElement);
			if (input) {
				if (input.name.toLowerCase() === "image") {
					variantFormData.append(input.name, input.files[0]);
				} else {
					variantFormData.append(input.name, input.value);
				}
			}
		}
		if (this.descriptionModal.descriptions.length > 0 && this.descriptionModal.formVisible) {
			variantFormData.append("Descriptions[0].Text", this.descriptionModal.descriptions[0].text);
			if (this.descriptionModal.descriptions[0].imageFile) {
				variantFormData.append("DescriptionsImage", this.descriptionModal.descriptions[0].imageFile);
			}
		} else if (this.descriptionModal.descriptions.length > 0
			|| this.descriptionModal.descriptionTextArea.value.trim()) {
			variantFormData.append("Descriptions[0].Text", this.descriptionModal.descriptionTextArea.value.trim());
		}

		return { valid, variantFormData };
	}

	protected addUploadImageChangeEventListener() {
		const fileInput: HTMLInputElement = document.querySelector('input[name="Image"]');
		const fileDisplayArea: HTMLDivElement = document.querySelector('#pic');
		const sizeLimit: number = 1024 * 1024; // 1MB

		fileInput.onchange = () => {
			fileDisplayArea.innerHTML = "";
			fileInput.style.display = "block";
			fileInput.required = true;
			if (fileInput.value && fileInput.files[0]?.size < sizeLimit) {
				let file = fileInput.files[0];

				let reader = new FileReader();

				reader.onload = function () {
					fileDisplayArea.classList.add('open');
					fileDisplayArea.appendChild(createImg(reader.result.toString()));
				}

				reader.readAsDataURL(file);
			} else {
				fileInput.value = "";
				fileDisplayArea.classList.remove('open');
			}
		}
	}

	protected addDescriptionModalEventsListeners(modal: HTMLDivElement) {
		this.descriptionModal = new DescriptionModal();
		const checkbox: HTMLInputElement = modal.querySelector("#info");
		const addImg: HTMLImageElement = modal.querySelector('img[alt="add icon"]');

		Mediator.on("descriptionModalClosed", () => {
			if (this.descriptionModal.formVisible) {
				if (this.descriptionModal.descriptions.length > 0) {
					checkbox.checked = true;
				} else {
					checkbox.checked = false;
				}
			}
		});

		addImg.onclick = () => {
			this.descriptionModal.openModal();
		}
	}

	protected addInputNumberEventsListeners(modalSelector: string) {
		const form: HTMLFormElement = document.querySelector(`${modalSelector} .modal form`);
		const inputBaseCost: HTMLInputElement = form.querySelector(`input[name=baseCost]`);
		const labelCostWithVat: HTMLLabelElement = form.querySelector(`label#vat`);
		const inputCost: HTMLInputElement = form.querySelector(`input[name=cost]`);
		const inputDomainStoreCost: HTMLInputElement = form.querySelector(`input[name=domainStoreCost]`);
		const inputTime: HTMLInputElement = form.querySelector(`input[name=productionTime]`);

		inputBaseCost.onchange = () => {
			this.changePriceWithVat(inputBaseCost, labelCostWithVat);
		};

		// base cost
		const imgIncreaseBaseCost: HTMLImageElement = form.querySelector(`input[name=baseCost] + div.input-number img[alt='increase icon']`);
		imgIncreaseBaseCost.onclick = () => {
			this.increaseInputValue(inputBaseCost, 0);
			this.changePriceWithVat(inputBaseCost, labelCostWithVat);
		}

		this.addArrowKeysEventListeners (inputBaseCost, labelCostWithVat);

		const imgDecreaseBaseCost: HTMLImageElement = form.querySelector(`input[name=baseCost] + div.input-number img[alt='decrease icon']`);
		imgDecreaseBaseCost.onclick = () => {
			this.decreaseInputValue(inputBaseCost, 0);
			this.changePriceWithVat(inputBaseCost, labelCostWithVat);
		}

		// cost
		const imgIncreaseCost: HTMLImageElement = form.querySelector(`input[name=cost] + div.input-number img[alt='increase icon']`);
		imgIncreaseCost.onclick = () => {
			this.increaseInputValue(inputCost, 0);
		}

		this.addArrowKeysEventListeners (inputCost);

		const imgDecreaseCost: HTMLImageElement = form.querySelector(`input[name=cost] + div.input-number img[alt='decrease icon']`);
		imgDecreaseCost.onclick = () => {
			this.decreaseInputValue(inputCost, 0);
		}

		// domain store cost
		const imgIncreaseDomainStoreCost: HTMLImageElement = form.querySelector(`input[name=domainStoreCost] + div.input-number img[alt='increase icon']`);
		imgIncreaseDomainStoreCost.onclick = () => {
			this.increaseInputValue(inputDomainStoreCost, 0);
		}

		this.addArrowKeysEventListeners (inputDomainStoreCost);

		const imgDecreaseDomainStoreCost: HTMLImageElement = form.querySelector(`input[name=domainStoreCost] + div.input-number img[alt='decrease icon']`);
		imgDecreaseDomainStoreCost.onclick = () => {
			this.decreaseInputValue(inputDomainStoreCost, 0);
		}

		// production time
		const imgIncreaseTime: HTMLImageElement = form.querySelector(`input[name=productionTime] + div.input-number img[alt='increase icon']`);
		imgIncreaseTime.onclick = () => {
			this.increaseInputValue(inputTime, 1);
		}

		const imgDecreaseTime: HTMLImageElement = form.querySelector(`input[name=productionTime] + div.input-number img[alt='decrease icon']`);
		imgDecreaseTime.onclick = () => {
			this.decreaseInputValue(inputTime, 1);
		}
	}

	private changePriceWithVat(inputBaseCost: HTMLInputElement, labelCostWithVat: HTMLLabelElement) {
		if (inputBaseCost.checkValidity()) {
			let vat: number = parseFloat(labelCostWithVat.dataset.vat.replace(',', '.'));
			let value: number = parseFloat(inputBaseCost.value.replace(',', '.'));
			const hasComma = LanguageUtil.getCurrentLanguage() == 'da';

			if (!isNaN(value)) {
				let labelCostWithVatText = `${value * (1 + vat)}`;
				if (hasComma) {
					labelCostWithVatText = labelCostWithVatText.replace('\.', ',')
				}
				labelCostWithVat.innerText = labelCostWithVatText;
			} else {
				labelCostWithVat.innerText = "";
			}
		}
	}

	private addArrowKeysEventListeners (input: HTMLInputElement, labelCostWithVat?: HTMLLabelElement): void {
		input.onkeydown = (event) => {
			if (!input.checkValidity() || event.defaultPrevented) {
				return;
			}

			switch (event.key) {
				case "Down": // IE/Edge specific value
				case "ArrowDown":
					this.decreaseInputValue(input, 0);
					break;
				case "Up": // IE/Edge specific value
				case "ArrowUp":
					this.increaseInputValue(input, 0);
					break;
				default:
					return;
			}

			if (labelCostWithVat) {
				this.changePriceWithVat(input, labelCostWithVat);
			}

			event.preventDefault();
		}
	}

	private increaseInputValue(input: HTMLInputElement, min: number): void {
		if (input.value) {
			if (input.value) {
				if (!input.checkValidity()) {
					return;
				}

				let value: number = parseFloat(input.value.replace(',', '.'));
				const hasComma = LanguageUtil.getCurrentLanguage() == 'da';

			if (value >= min) {
				if (min == 1) {
					value++;
					input.value = value.toString();
				} else {
					value += 0.5;
					input.value = value.toFixed(2).toString();
				}
				}

				if (hasComma) {
					input.value = input.value.replace('\.', ',');
				}
			} else {
				input.value = "1";
			}
		}
	}

	private decreaseInputValue(input: HTMLInputElement, min: number): void {
		if (input.value) {
			if (!input.checkValidity()) {
				return;
			}

			let value: number = parseFloat(input.value.replace(',', '.'));
			const hasComma = LanguageUtil.getCurrentLanguage() == 'da';

			if (value > min) {
				if (min == 1) {
					value--;
					input.value = value.toString();
				} else {
					value -= 0.5;
					input.value = value.toFixed(2).toString();
				}
			}

			if (hasComma) {
				input.value = input.value.replace('\.', ',');
			}
		} else {
			input.value = "1";
		}
	}
}
